/**
 * LobbyFooter Component for Poker Reborn
 *
 * Bottom navigation bar in the lobby with:
 * - Help button (left)
 * - Friends button (left)
 * - Bonus button (center, prominent CTA)
 * - Inbox button (right)
 *
 * @see docs/screens/lobby/LOBBY_FOOTER.md - Visual specs, dimensions, behavior
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, GOLD_BUTTON_STYLE } from '@/components/common/Button';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Bar dimensions */
  bar: {
    height: 48,
    heightMobile: 44,
    backgroundColor: 0x000000,
    backgroundAlpha: 0.6,
    borderColor: 0x2a2a40,
    borderAlpha: 0.5,
    borderWidth: 1,
    padding: 12,
  },

  /** Standard button dimensions */
  button: {
    width: 80,
    widthMobile: 40,
    height: 36,
    fontSize: 14,
    gap: 8,
  },

  /** Bonus CTA button (larger, prominent) */
  bonusButton: {
    width: 120,
    widthMobile: 80,
    height: 44,
    heightMobile: 40,
    fontSize: 16,
    fontSizeMobile: 14,
  },

  /** Responsive breakpoints */
  breakpoints: {
    mobile: 480,
  },
};

// =============================================================================
// LOBBYFOOTER COMPONENT
// =============================================================================

export class LobbyFooter extends Container {
  private screenWidth: number;
  private screenHeight: number;

  // Components
  private background: Graphics;
  private helpButton: Button;
  private friendsButton: Button;
  private bonusButton: Button;
  private inboxButton: Button;

  // Inbox badge
  private inboxBadge: Container | null = null;
  private inboxBadgeText: Text | null = null;

  // Bonus badge (gold dot when bonus is available)
  private bonusBadge: Container | null = null;

  // Callbacks
  public onHelp?: () => void;
  public onFriends?: () => void;
  public onBonus?: () => void;
  public onInbox?: () => void;

  constructor(width: number, height: number) {
    super();
    this.screenWidth = width;
    this.screenHeight = height;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create Help button (left)
    this.helpButton = new Button({
      label: 'Help',
      width: STYLES.button.width,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.helpButton.onClick = () => this.onHelp?.();
    this.addChild(this.helpButton);

    // Create Friends button (left, next to Help)
    this.friendsButton = new Button({
      label: 'Friends',
      width: STYLES.button.width,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.friendsButton.onClick = () => this.onFriends?.();
    this.friendsButton.disabled = true;
    this.addChild(this.friendsButton);

    // Create Bonus button (center, prominent CTA with gold glow)
    this.bonusButton = new Button({
      label: 'BONUS',
      width: STYLES.bonusButton.width,
      height: STYLES.bonusButton.height,
      style: {
        ...GOLD_BUTTON_STYLE,
        fontSize: STYLES.bonusButton.fontSize,
      },
    });
    this.bonusButton.onClick = () => this.onBonus?.();
    this.addChild(this.bonusButton);

    // Create Inbox button (right)
    this.inboxButton = new Button({
      label: 'Inbox',
      width: STYLES.button.width,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.inboxButton.onClick = () => this.onInbox?.();
    this.addChild(this.inboxButton);

    // Initial layout
    this.layout();
  }

  /**
   * Resize the footer bar
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.layout();
  }

  /**
   * Get the bar height for layout calculations
   */
  getBarHeight(): number {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    return isMobile ? STYLES.bar.heightMobile : STYLES.bar.height;
  }

  /**
   * Clean up
   */
  destroy(): void {
    super.destroy();
  }

  // ===========================================================================
  // PUBLIC METHODS
  // ===========================================================================

  /** Show/hide gold badge on bonus button when bonus is claimable */
  setBonusBadge(available: boolean): void {
    if (!available) {
      if (this.bonusBadge) {
        this.bonusBadge.visible = false;
      }
      return;
    }

    if (!this.bonusBadge) {
      this.bonusBadge = this.createBonusBadge();
      this.bonusButton.addChild(this.bonusBadge);
    }

    this.bonusBadge.visible = true;
    this.positionBonusBadge();
  }

  /** Update unread badge on inbox button */
  setInboxBadge(count: number): void {
    if (count <= 0) {
      if (this.inboxBadge) {
        this.inboxBadge.visible = false;
      }
      return;
    }

    if (!this.inboxBadge) {
      this.inboxBadge = this.createBadge();
      this.inboxButton.addChild(this.inboxBadge);
    }

    this.inboxBadge.visible = true;
    if (this.inboxBadgeText) {
      this.inboxBadgeText.text = count > 9 ? '9+' : String(count);
    }

    // Reposition badge to top-right of button
    this.inboxBadge.x = this.inboxButton.buttonWidth - 8;
    this.inboxBadge.y = 4;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private createBonusBadge(): Container {
    const badgeContainer = new Container();

    // Gold glowing dot
    const outerGlow = new Graphics();
    outerGlow.circle(0, 0, 8);
    outerGlow.fill({ color: 0xffd700, alpha: 0.4 });
    badgeContainer.addChild(outerGlow);

    const innerDot = new Graphics();
    innerDot.circle(0, 0, 5);
    innerDot.fill(0xffd700);
    badgeContainer.addChild(innerDot);

    return badgeContainer;
  }

  private positionBonusBadge(): void {
    if (this.bonusBadge && this.bonusBadge.visible) {
      this.bonusBadge.x = this.bonusButton.buttonWidth - 6;
      this.bonusBadge.y = 4;
    }
  }

  private createBadge(): Container {
    const badgeContainer = new Container();

    // Red circle background
    const badgeBackground = new Graphics();
    badgeBackground.circle(0, 0, 10);
    badgeBackground.fill(0xe53935);
    badgeContainer.addChild(badgeBackground);

    // Count text
    this.inboxBadgeText = new Text({
      text: '0',
      style: {
        fill: 0xffffff,
        fontSize: 12,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    this.inboxBadgeText.anchor.set(0.5, 0.5);
    badgeContainer.addChild(this.inboxBadgeText);

    return badgeContainer;
  }

  private layout(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    const barHeight = isMobile ? STYLES.bar.heightMobile : STYLES.bar.height;

    // Draw background
    this.background.clear();

    // Draw top border first (above main background)
    this.background.rect(0, 0, this.screenWidth, STYLES.bar.borderWidth);
    this.background.fill({ color: STYLES.bar.borderColor, alpha: STYLES.bar.borderAlpha });

    // Draw main background
    this.background.rect(0, 0, this.screenWidth, barHeight);
    this.background.fill({ color: STYLES.bar.backgroundColor, alpha: STYLES.bar.backgroundAlpha });

    // Button dimensions based on breakpoint
    const buttonWidth = isMobile ? STYLES.button.widthMobile : STYLES.button.width;
    const buttonHeight = STYLES.button.height;
    const bonusWidth = isMobile ? STYLES.bonusButton.widthMobile : STYLES.bonusButton.width;
    const bonusHeight = isMobile ? STYLES.bonusButton.heightMobile : STYLES.bonusButton.height;

    // Vertical centering
    const buttonY = (barHeight - buttonHeight) / 2;
    const bonusY = (barHeight - bonusHeight) / 2;

    // Update button labels and sizes for responsive
    if (isMobile) {
      this.helpButton.label = '?';
      this.helpButton.resize(buttonWidth, buttonHeight);

      this.friendsButton.label = 'F';
      this.friendsButton.resize(buttonWidth, buttonHeight);

      this.bonusButton.label = 'B';
      this.bonusButton.resize(bonusWidth, bonusHeight);

      this.inboxButton.label = 'I';
      this.inboxButton.resize(buttonWidth, buttonHeight);
    } else {
      this.helpButton.label = 'Help';
      this.helpButton.resize(buttonWidth, buttonHeight);

      this.friendsButton.label = 'Friends';
      this.friendsButton.resize(buttonWidth, buttonHeight);

      this.bonusButton.label = 'BONUS';
      this.bonusButton.resize(bonusWidth, bonusHeight);

      this.inboxButton.label = 'Inbox';
      this.inboxButton.resize(buttonWidth, buttonHeight);
    }

    // === LEFT ZONE: Help + Friends ===
    // Help button (leftmost)
    this.helpButton.x = STYLES.bar.padding;
    this.helpButton.y = buttonY;

    // Friends button (right of Help)
    this.friendsButton.x = this.helpButton.x + buttonWidth + STYLES.button.gap;
    this.friendsButton.y = buttonY;

    // === CENTER ZONE: Bonus ===
    // Bonus button centered horizontally
    this.bonusButton.x = (this.screenWidth - bonusWidth) / 2;
    this.bonusButton.y = bonusY;

    // === RIGHT ZONE: Inbox ===
    // Inbox button (rightmost)
    this.inboxButton.x = this.screenWidth - buttonWidth - STYLES.bar.padding;
    this.inboxButton.y = buttonY;

    // Reposition badges after button layout
    if (this.inboxBadge && this.inboxBadge.visible) {
      this.inboxBadge.x = this.inboxButton.buttonWidth - 8;
      this.inboxBadge.y = 4;
    }
    this.positionBonusBadge();
  }
}
